import type {
  BalanceTransactionEntry,
  CreditLotEntry,
  V1BillingStatement,
} from '@nadoutong/contracts';
import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { PlatformApiError } from '../api/client.ts';
import {
  useBalance,
  useCredits,
  useDisplayRate,
  useRedeemGiftCard,
  useStatements,
  useTransactions,
} from '../api/useBilling.ts';
import { GiftBalanceLine } from '../components/GiftBalanceLine.tsx';
import { PanelError } from '../components/PanelError.tsx';
import { coerceErrorMessage, useToast } from '../components/Toast.tsx';
import {
  daysUntil,
  formatCnyCents,
  formatDisplayRateHint,
  formatTimestamp,
  formatUsdMicrosOrCentsAsCnyDisplay,
  formatUsdMicrosOrCentsAsCnyMetered,
  usdMicrosFromCentsFallback,
} from '../components/formatters.ts';
import { hasPermission, useSession } from '../hooks/useSession.tsx';
import { useT } from '../i18n/index.ts';
import {
  Button,
  Chip,
  Dialog,
  EmptyState,
  FormField,
  Input,
  PageHeader,
  Panel,
  Skeleton,
  TBody,
  Table,
  TableSkeleton,
  Td,
  Th,
  THead,
  Tr,
  buttonClass,
  cx,
} from '../ui/index.ts';

/**
 * `/billing` — balance hero, gift-card redeem, and the transaction
 * ledger (epic #897 PR 9, local Dispatch Deck rebuild of the
 * console-shared factory page).
 *
 * Money rules (DESIGN.md §2): the balance is the hero numeral
 * (`text-stat-lg` mono); inside the ledger table credit renders `ok`
 * and debit renders `bad`; every other amount stays neutral `paper`.
 *
 * Interaction contract:
 *   - The `org:billing:read` forbidden state is an EmptyState WITH a
 *     CTA back to /dashboard (the old page was a dead end).
 *   - Redeem success reports through the toast only (+ invalidated
 *     balance/transactions); failures render inline under the field —
 *     the old inline+toast double-report is gone (single-channel).
 *   - Ledger paging keeps the `rows.length === PAGE_SIZE` hasMore
 *     heuristic (the API has no total), improved: "older" now disables
 *     on a short page instead of offering a clickable empty page.
 */

const PAGE_SIZE = 20;

type TranslateFn = ReturnType<typeof useT>;

export function Billing(): JSX.Element {
  const { user } = useSession();
  if (!hasPermission(user, 'org:billing:read')) {
    return <BillingForbidden />;
  }
  return <BillingContent />;
}

function BillingForbidden(): JSX.Element {
  const t = useT();
  return (
    <section data-testid="billing-page" className="flex flex-col gap-4">
      <PageHeader title={t('billing.title')} sub={t('billing.subtitle')} testId="billing-header" />
      <div data-testid="billing-forbidden" className="rounded-lg border border-line-soft bg-card">
        <EmptyState
          title={t('billing.forbidden.title')}
          hint={t('billing.forbidden.body')}
          action={
            <Link
              to="/dashboard"
              className={buttonClass('secondary')}
              data-testid="billing-forbidden-cta"
            >
              {t('billing.forbidden.cta')}
            </Link>
          }
        />
      </div>
    </section>
  );
}

function BillingContent(): JSX.Element {
  const t = useT();
  const { user } = useSession();
  const displayRateQ = useDisplayRate();
  const cnyPerUsd = displayRateQ.data?.cnyPerUsd ?? null;
  const canManageBilling = hasPermission(user, 'org:billing:manage');

  return (
    <section data-testid="billing-page" className="flex flex-col gap-4">
      <PageHeader title={t('billing.title')} sub={t('billing.subtitle')} testId="billing-header" />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <BalancePanel cnyPerUsd={cnyPerUsd} canManageBilling={canManageBilling} />
        {canManageBilling ? <RedeemPanel cnyPerUsd={cnyPerUsd} /> : null}
      </div>

      <CreditsPanel cnyPerUsd={cnyPerUsd} />

      <StatementsPanel cnyPerUsd={cnyPerUsd} />

      {canManageBilling ? <TransactionsPanel cnyPerUsd={cnyPerUsd} /> : null}
    </section>
  );
}

interface BalancePanelProps {
  readonly cnyPerUsd: number | null;
  readonly canManageBilling: boolean;
}

function BalancePanel({ cnyPerUsd, canManageBilling }: BalancePanelProps): JSX.Element {
  const t = useT();
  const balanceQ = useBalance();
  const balance = balanceQ.data;
  const funded =
    balance !== undefined &&
    usdMicrosFromCentsFallback(balance.balanceUsdMicros, balance.balanceUsdCents) > 0;
  const rateHint = formatDisplayRateHint(cnyPerUsd);

  return (
    <Panel
      caption={t('billing.balance.title')}
      padded
      testId="billing-balance-card"
      actions={
        balance !== undefined ? (
          <Chip tone={funded ? 'ok' : 'warn'} testId="billing-balance-chip">
            {funded ? t('billing.balance.status.funded') : t('billing.balance.status.zero')}
          </Chip>
        ) : undefined
      }
    >
      {balanceQ.isLoading ? (
        <Skeleton className="h-24" testId="billing-balance-loading" />
      ) : balanceQ.isError ? (
        <PanelError
          message={t('billing.balance.error')}
          retryLabel={t('billing.retry')}
          onRetry={() => {
            void balanceQ.refetch();
          }}
          testId="billing-balance-error"
        />
      ) : balance !== undefined ? (
        <div className="flex flex-col gap-3">
          <span className="font-mono text-stat-lg text-paper" data-testid="billing-balance-value">
            {formatUsdMicrosOrCentsAsCnyDisplay(
              balance.balanceUsdMicros,
              balance.balanceUsdCents,
              cnyPerUsd,
            )}
          </span>
          <GiftBalanceLine balance={balance} cnyPerUsd={cnyPerUsd} testId="billing-balance-gift" />
          <p className="m-0 font-mono text-micro text-paper-low">
            {t('billing.balance.updated', { timestamp: formatTimestamp(balance.updatedAt) })}
            {rateHint === '—' ? '' : ` · ${rateHint}`}
          </p>
          {canManageBilling ? (
            <Link
              to="/billing/topup"
              className={cx(buttonClass('primary'), 'w-fit')}
              data-testid="billing-topup-entry"
            >
              {t('billing.topup.entry')}
            </Link>
          ) : null}
        </div>
      ) : null}
    </Panel>
  );
}

/**
 * The balance, decomposed into the batches it is actually made of.
 *
 * Read with `org:billing:read` — same gate as the balance, because this is
 * the same number seen from closer up, not a movement ledger (that is
 * `TransactionsPanel`, which needs `org:billing:manage`).
 *
 * A 404 renders nothing at all: `/v1/billing/credits` is newer than this
 * page, so a console running against a rolled-back platform must degrade to
 * "no breakdown" rather than to a red error box for a route that simply
 * isn't there yet. Every other failure keeps the normal retry affordance.
 */
function CreditsPanel({ cnyPerUsd }: { readonly cnyPerUsd: number | null }): JSX.Element | null {
  const t = useT();
  const creditsQ = useCredits();

  if (creditsQ.isLoading) {
    return null;
  }
  if (creditsQ.isError) {
    if (creditsQ.error instanceof PlatformApiError && creditsQ.error.status === 404) {
      return null;
    }
    return (
      <Panel caption={t('billing.credits.title')} testId="billing-credits-card">
        <div className="p-4">
          <PanelError
            message={t('billing.credits.error')}
            retryLabel={t('billing.retry')}
            onRetry={() => {
              void creditsQ.refetch();
            }}
            testId="billing-credits-error"
          />
        </div>
      </Panel>
    );
  }

  const lots = creditsQ.data?.lots ?? [];
  return (
    <Panel caption={t('billing.credits.title')} testId="billing-credits-card">
      <div className="flex flex-col gap-3 p-4">
        <p className="m-0 text-micro text-paper-low">{t('billing.credits.subtitle')}</p>
        {lots.length === 0 ? (
          <EmptyState title={t('billing.credits.empty')} />
        ) : (
          <Table testId="billing-credits-table">
            <THead>
              <Tr interactive={false}>
                <Th>{t('billing.credits.column.kind')}</Th>
                <Th num>{t('billing.credits.column.remaining')}</Th>
                <Th num>{t('billing.credits.column.amount')}</Th>
                <Th>{t('billing.credits.column.expires')}</Th>
                <Th>{t('billing.credits.column.issued')}</Th>
              </Tr>
            </THead>
            <TBody>
              {lots.map((lot) => (
                <CreditLotRow key={lot.id} lot={lot} cnyPerUsd={cnyPerUsd} />
              ))}
            </TBody>
          </Table>
        )}
      </div>
    </Panel>
  );
}

function CreditLotRow({
  lot,
  cnyPerUsd,
}: {
  readonly lot: CreditLotEntry;
  readonly cnyPerUsd: number | null;
}): JSX.Element {
  const t = useT();
  const isPromo = lot.kind === 'promo';
  const days = daysUntil(lot.expiresAt);
  return (
    <Tr data-testid={`billing-credit-row-${lot.id}`}>
      <Td label={t('billing.credits.column.kind')}>
        <Chip tone={isPromo ? 'warn' : 'ok'} testId={`billing-credit-kind-${lot.id}`}>
          {isPromo ? t('billing.credits.kind.promo') : t('billing.credits.kind.cash')}
        </Chip>
      </Td>
      <Td num heading label={t('billing.credits.column.remaining')}>
        {formatUsdMicrosOrCentsAsCnyDisplay(
          lot.remainingUsdMicros,
          lot.remainingUsdCents,
          cnyPerUsd,
        )}
      </Td>
      <Td num label={t('billing.credits.column.amount')}>
        {formatUsdMicrosOrCentsAsCnyDisplay(lot.amountUsdMicros, lot.amountUsdCents, cnyPerUsd)}
      </Td>
      <Td
        mono
        label={t('billing.credits.column.expires')}
        data-testid={`billing-credit-expires-${lot.id}`}
      >
        {lot.expiresAt === null ? (
          <span className="text-paper-low">{t('billing.credits.never_expires')}</span>
        ) : (
          // Amber once inside a week: the point of this column is to be
          // noticed before the sweep takes the money back.
          <span className={days !== null && days <= 7 ? 'text-warn' : undefined}>
            {formatTimestamp(lot.expiresAt)}
          </span>
        )}
      </Td>
      <Td mono label={t('billing.credits.column.issued')}>
        {formatTimestamp(lot.issuedAt)}
      </Td>
    </Tr>
  );
}

function RedeemPanel({ cnyPerUsd }: { readonly cnyPerUsd: number | null }): JSX.Element {
  const t = useT();
  const redeemM = useRedeemGiftCard();
  const toast = useToast();
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    const trimmed = code.trim();
    if (trimmed.length === 0) {
      setError(t('billing.redeem.error.code_required'));
      return;
    }
    try {
      const result = await redeemM.mutateAsync({ code: trimmed });
      setCode('');
      toast.success(
        t('billing.redeem.toast.success', {
          amount: formatCnyCents(result.amountCnyCents),
          balance: formatUsdMicrosOrCentsAsCnyDisplay(
            result.newBalanceUsdMicros,
            result.newBalanceUsdCents,
            cnyPerUsd,
          ),
        }),
      );
    } catch (err) {
      setError(
        err instanceof PlatformApiError ? `${err.code}: ${err.message}` : coerceErrorMessage(err),
      );
    }
  }

  return (
    <Panel caption={t('billing.redeem.title')} padded testId="billing-redeem-card">
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          void handleSubmit(e);
        }}
        noValidate
      >
        <FormField
          label={t('billing.redeem.code_label')}
          fieldId="billing-redeem-code"
          error={
            error !== null ? <span data-testid="billing-redeem-error">{error}</span> : undefined
          }
        >
          <Input
            id="billing-redeem-code"
            mono
            value={code}
            onChange={(e) => {
              setCode(e.currentTarget.value);
              setError(null);
            }}
            disabled={redeemM.isPending}
            invalid={error !== null}
            aria-describedby={error !== null ? 'billing-redeem-code-error' : undefined}
            placeholder={t('billing.redeem.code_placeholder')}
            autoComplete="off"
            data-testid="billing-redeem-code"
          />
        </FormField>
        <div className="flex items-center justify-end">
          <Button
            type="submit"
            variant="primary"
            loading={redeemM.isPending}
            data-testid="billing-redeem-submit"
          >
            {t('billing.redeem.submit')}
          </Button>
        </div>
      </form>
    </Panel>
  );
}

function TransactionsPanel({ cnyPerUsd }: { readonly cnyPerUsd: number | null }): JSX.Element {
  const t = useT();
  const [offset, setOffset] = useState(0);
  const txQ = useTransactions({ limit: PAGE_SIZE, offset });
  const balanceQ = useBalance();
  const rows = txQ.data?.transactions ?? [];
  // The transactions API has no total; a full page implies there may be
  // an older page. A short page disables "older" outright — no more
  // clickable empty page.
  const hasMore = rows.length === PAGE_SIZE;

  // Running balance is only computable on the first page (offset === 0)
  // because we walk backwards from the current balance through each
  // most-recent transaction. On deeper pages we'd need every in-between
  // amount, which the server doesn't return; cells render "—" instead.
  const runningBalances = computeRunningBalances(
    rows,
    offset === 0 && balanceQ.data !== undefined
      ? usdMicrosFromCentsFallback(balanceQ.data.balanceUsdMicros, balanceQ.data.balanceUsdCents)
      : null,
  );

  return (
    <Panel
      caption={t('billing.transactions.title')}
      testId="billing-transactions-card"
      actions={
        <span
          className="font-mono text-micro text-paper-low"
          data-testid="billing-transactions-offset"
        >
          {offset === 0
            ? t('billing.transactions.latest')
            : t('billing.transactions.page', { page: String(Math.floor(offset / PAGE_SIZE) + 1) })}
        </span>
      }
    >
      <div className="flex flex-col gap-3 p-4">
        {txQ.isLoading ? (
          <TableSkeleton rows={4} testId="billing-transactions-loading" />
        ) : txQ.isError ? (
          <PanelError
            message={t('billing.transactions.error')}
            retryLabel={t('billing.retry')}
            onRetry={() => {
              void txQ.refetch();
            }}
            testId="billing-transactions-error"
          />
        ) : rows.length === 0 ? (
          <EmptyState testId="billing-transactions-empty" title={t('billing.transactions.empty')} />
        ) : (
          <Table testId="billing-transactions-table">
            <THead>
              <Tr interactive={false}>
                <Th>{t('billing.transactions.column.date')}</Th>
                <Th>{t('billing.transactions.column.type')}</Th>
                <Th num>{t('billing.transactions.column.amount_usd')}</Th>
                <Th num>{t('billing.transactions.column.running')}</Th>
              </Tr>
            </THead>
            <TBody>
              {rows.map((tx, idx) => (
                <TransactionRow
                  key={tx.id}
                  tx={tx}
                  runningBalanceMicros={runningBalances[idx] ?? null}
                  cnyPerUsd={cnyPerUsd}
                />
              ))}
            </TBody>
          </Table>
        )}

        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            disabled={offset === 0 || txQ.isFetching}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            data-testid="billing-transactions-prev"
          >
            {t('billing.transactions.newer')}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={!hasMore || txQ.isFetching}
            onClick={() => setOffset(offset + PAGE_SIZE)}
            data-testid="billing-transactions-next"
          >
            {t('billing.transactions.older')}
          </Button>
        </div>
      </div>
    </Panel>
  );
}

interface TransactionRowProps {
  readonly tx: BalanceTransactionEntry;
  readonly runningBalanceMicros: number | null;
  readonly cnyPerUsd: number | null;
}

function TransactionRow({ tx, runningBalanceMicros, cnyPerUsd }: TransactionRowProps): JSX.Element {
  const t = useT();
  const amountUsdMicros = usdMicrosFromCentsFallback(tx.amountUsdMicros, tx.amountUsdCents);
  const isCredit = amountUsdMicros > 0;
  return (
    <Tr data-testid={`billing-tx-row-${tx.id}`}>
      <Td mono heading>
        {formatTimestamp(tx.createdAt)}
      </Td>
      <Td label={t('billing.transactions.column.type')}>
        <Chip tone={isCredit ? 'ok' : 'neutral'} testId={`billing-tx-type-${tx.id}`}>
          {transactionTypeLabel(t, tx.type)}
        </Chip>
      </Td>
      <Td
        num
        label={t('billing.transactions.column.amount_usd')}
        data-testid={`billing-tx-amount-${tx.id}`}
      >
        <span className={isCredit ? 'text-ok' : 'text-bad'}>
          {formatUsdMicrosOrCentsAsCnyMetered(
            tx.amountUsdMicros,
            tx.amountUsdCents,
            cnyPerUsd,
            true,
          )}
        </span>
      </Td>
      <Td
        num
        label={t('billing.transactions.column.running')}
        data-testid={`billing-tx-running-${tx.id}`}
      >
        {runningBalanceMicros !== null
          ? formatUsdMicrosOrCentsAsCnyDisplay(runningBalanceMicros, null, cnyPerUsd)
          : '—'}
      </Td>
    </Tr>
  );
}

function transactionTypeLabel(t: TranslateFn, txType: string): string {
  switch (txType) {
    case 'charge':
      return t('billing.transactions.type.charge');
    case 'gift_card_redeem':
      return t('billing.transactions.type.gift_card_redeem');
    case 'gift_direct_issue':
      return t('billing.transactions.type.gift_direct_issue');
    case 'gift_expiry_reclaim':
      return t('billing.transactions.type.gift_expiry_reclaim');
    case 'adjustment':
      return t('billing.transactions.type.adjustment');
    case 'topup':
      return t('billing.transactions.type.topup');
    case 'external_topup':
      return t('billing.transactions.type.external_topup');
    default:
      return txType.replace(/_/g, ' ');
  }
}

/**
 * Walk the DESC-ordered transaction list and compute the running
 * balance AFTER each row. Row 0 (most-recent) settles into the passed
 * `currentBalanceMicros`; row N's running balance = row N-1's running
 * balance minus row N-1's amount.
 *
 * When `currentBalanceMicros` is null (non-first page, or the balance
 * query hasn't loaded) the function returns an all-null array — the UI
 * falls back to rendering "—".
 */
function computeRunningBalances(
  rows: readonly BalanceTransactionEntry[],
  currentBalanceMicros: number | null,
): readonly (number | null)[] {
  if (currentBalanceMicros === null) {
    return rows.map(() => null);
  }
  const out: number[] = [];
  let runner = currentBalanceMicros;
  for (const tx of rows) {
    out.push(runner);
    runner -= usdMicrosFromCentsFallback(tx.amountUsdMicros, tx.amountUsdCents);
  }
  return out;
}

/**
 * Billing statements (epic #1033 E4-C9). Only credit customers accrue
 * statements, so a prepaid-only tenant's `/v1/billing/statements` returns an
 * empty list and this panel renders nothing (no surface for a tenant who will
 * never have a statement). Consumption is labelled INFORMATIONAL — the amount
 * owed is the closing AR, not gross usage. Overdue is derived client-side from
 * the stored `status`/`dueDate`; the wire never carries an overdue flag.
 */
interface StatementDisplay {
  readonly tone: 'ok' | 'warn' | 'bad';
  readonly labelKey:
    | 'billing.statements.status.settled'
    | 'billing.statements.status.open'
    | 'billing.statements.status.overdue';
}

function todayYmd(): string {
  return new Date().toISOString().slice(0, 10);
}

function deriveStatementDisplay(statement: V1BillingStatement): StatementDisplay {
  if (statement.status === 'closed') {
    return { tone: 'ok', labelKey: 'billing.statements.status.settled' };
  }
  if (statement.dueDate < todayYmd()) {
    return { tone: 'bad', labelKey: 'billing.statements.status.overdue' };
  }
  return { tone: 'warn', labelKey: 'billing.statements.status.open' };
}

function StatementsPanel({ cnyPerUsd }: { readonly cnyPerUsd: number | null }): JSX.Element | null {
  const t = useT();
  const statementsQ = useStatements();
  const [selected, setSelected] = useState<V1BillingStatement | null>(null);

  if (statementsQ.isLoading) {
    return null;
  }
  if (statementsQ.isError) {
    return (
      <Panel caption={t('billing.statements.title')} testId="billing-statements-card">
        <div className="p-4">
          <PanelError
            message={t('billing.statements.error')}
            retryLabel={t('billing.retry')}
            onRetry={() => {
              void statementsQ.refetch();
            }}
            testId="billing-statements-error"
          />
        </div>
      </Panel>
    );
  }

  const statements = statementsQ.data?.statements ?? [];
  if (statements.length === 0) {
    return null;
  }

  return (
    <Panel caption={t('billing.statements.title')} testId="billing-statements-card">
      <div className="flex flex-col gap-3 p-4">
        <p className="m-0 text-micro text-paper-low">{t('billing.statements.subtitle')}</p>
        <Table testId="billing-statements-table">
          <THead>
            <Tr interactive={false}>
              <Th>{t('billing.statements.column.period')}</Th>
              <Th num>{t('billing.statements.column.consumption')}</Th>
              <Th num>{t('billing.statements.column.closing_ar')}</Th>
              <Th>{t('billing.statements.column.due')}</Th>
              <Th>{t('billing.statements.column.status')}</Th>
            </Tr>
          </THead>
          <TBody>
            {statements.map((statement) => (
              <StatementRow
                key={statement.id}
                statement={statement}
                cnyPerUsd={cnyPerUsd}
                onOpen={() => setSelected(statement)}
              />
            ))}
          </TBody>
        </Table>
      </div>
      <StatementDetailDialog
        statement={selected}
        cnyPerUsd={cnyPerUsd}
        onClose={() => setSelected(null)}
      />
    </Panel>
  );
}

interface StatementRowProps {
  readonly statement: V1BillingStatement;
  readonly cnyPerUsd: number | null;
  readonly onOpen: () => void;
}

function StatementRow({ statement, cnyPerUsd, onOpen }: StatementRowProps): JSX.Element {
  const t = useT();
  const display = deriveStatementDisplay(statement);
  return (
    <Tr
      data-testid={`billing-statement-row-${statement.id}`}
      className="cursor-pointer"
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen();
        }
      }}
    >
      <Td mono heading label={t('billing.statements.column.period')}>
        {formatStatementPeriod(statement)}
      </Td>
      <Td num label={t('billing.statements.column.consumption')}>
        {formatUsdMicrosOrCentsAsCnyDisplay(statement.consumptionUsdMicros, null, cnyPerUsd)}
      </Td>
      <Td
        num
        label={t('billing.statements.column.closing_ar')}
        data-testid={`billing-statement-ar-${statement.id}`}
      >
        {formatUsdMicrosOrCentsAsCnyDisplay(statement.closingArUsdMicros, null, cnyPerUsd)}
      </Td>
      <Td mono label={t('billing.statements.column.due')}>
        {statement.dueDate}
      </Td>
      <Td label={t('billing.statements.column.status')}>
        <Chip tone={display.tone} testId={`billing-statement-status-${statement.id}`}>
          {t(display.labelKey)}
        </Chip>
      </Td>
    </Tr>
  );
}

interface StatementDetailDialogProps {
  readonly statement: V1BillingStatement | null;
  readonly cnyPerUsd: number | null;
  readonly onClose: () => void;
}

function StatementDetailDialog({
  statement,
  cnyPerUsd,
  onClose,
}: StatementDetailDialogProps): JSX.Element | null {
  const t = useT();
  if (statement === null) {
    return null;
  }
  const rows: { readonly label: string; readonly value: string; readonly testId: string }[] = [
    {
      label: t('billing.statements.detail.period'),
      value: formatStatementPeriod(statement),
      testId: 'billing-statement-detail-period',
    },
    {
      label: t('billing.statements.detail.consumption'),
      value: formatUsdMicrosOrCentsAsCnyDisplay(statement.consumptionUsdMicros, null, cnyPerUsd),
      testId: 'billing-statement-detail-consumption',
    },
    {
      label: t('billing.statements.detail.opening_ar'),
      value: formatUsdMicrosOrCentsAsCnyDisplay(statement.openingArUsdMicros, null, cnyPerUsd),
      testId: 'billing-statement-detail-opening',
    },
    {
      label: t('billing.statements.detail.payments'),
      value: formatUsdMicrosOrCentsAsCnyDisplay(
        statement.paymentsAppliedUsdMicros,
        null,
        cnyPerUsd,
      ),
      testId: 'billing-statement-detail-payments',
    },
    {
      label: t('billing.statements.detail.closing_ar'),
      value: formatUsdMicrosOrCentsAsCnyDisplay(statement.closingArUsdMicros, null, cnyPerUsd),
      testId: 'billing-statement-detail-closing',
    },
    {
      label: t('billing.statements.detail.cny_reference'),
      value:
        statement.cnyReferenceClosingArCents === null
          ? '—'
          : formatCnyCents(statement.cnyReferenceClosingArCents),
      testId: 'billing-statement-detail-cny',
    },
    {
      label: t('billing.statements.detail.due'),
      value: statement.dueDate,
      testId: 'billing-statement-detail-due',
    },
  ];

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) {
          onClose();
        }
      }}
      title={t('billing.statements.detail.title')}
      description={t(deriveStatementDisplay(statement).labelKey)}
      closeLabel={t('billing.statements.detail.close')}
      size="md"
      testId="billing-statement-detail"
    >
      <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {rows.map((row) => (
          <div key={row.testId} className="flex flex-col gap-1">
            <dt className="m-0 text-micro text-paper-low">{row.label}</dt>
            <dd className="m-0 font-mono text-dense text-paper" data-testid={row.testId}>
              {row.value}
            </dd>
          </div>
        ))}
      </dl>
      <p className="m-0 mt-4 text-micro text-paper-low" data-testid="billing-statement-detail-note">
        {t('billing.statements.detail.consumption_hint')}
      </p>
    </Dialog>
  );
}

function formatStatementPeriod(statement: V1BillingStatement): string {
  return `${statement.periodStart} → ${statement.periodEnd}`;
}
